/* Flexacare — scroll-driven 3D product experience.
   Plain Three.js (r134 UMD, loaded on demand). No framework, no GSAP: the whole
   timeline is one damped scroll value, which is what makes it stop where you
   stop and reverse cleanly. */
(function () {
  'use strict';

  var root = document.querySelector('[data-f3d]');
  if (!root) return;

  var stage = root.querySelector('[data-f3d-stage]');
  var canvas = root.querySelector('[data-f3d-canvas]');
  var chapters = Array.prototype.slice.call(root.querySelectorAll('[data-f3d-chapter]'));
  var fallback = root.querySelector('[data-f3d-fallback]');
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // No WebGL, or the visitor asked for less motion: show the still frame and stop.
  function bailOut() {
    root.setAttribute('data-f3d-static', '');
    if (fallback) fallback.hidden = false;
    chapters.forEach(function (c) { c.style.opacity = ''; });
  }
  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
  }
  if (reduced || !hasWebGL()) { bailOut(); return; }

  var THREE_SRC = root.dataset.threeSrc;
  var PACK_SRC = root.dataset.packSrc || '';
  var loaded = false;

  // Only pay for the library once the section is actually near the viewport.
  var warmup = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting || loaded) return;
      loaded = true;
      warmup.disconnect();
      var s = document.createElement('script');
      s.src = THREE_SRC;
      s.async = true;
      s.onload = function () { try { boot(); } catch (err) { bailOut(); } };
      s.onerror = bailOut;
      document.head.appendChild(s);
    });
  }, { rootMargin: '150% 0px' });
  warmup.observe(root);

  /* ---------------- helpers ---------------- */
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };
  // Progress within a slice of the overall timeline, eased.
  var seg = function (p, a, b) { return clamp((p - a) / (b - a), 0, 1); };
  var ease = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };
  var easeOut = function (t) { return 1 - Math.pow(1 - t, 3); };
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  // Fades in over the first 18% of a slice and out over the last 18%.
  var band = function (p, a, b) {
    if (p < a || p > b) return 0;
    var t = (p - a) / (b - a);
    var inFade = a <= 0.001 ? 1 : clamp(t / 0.18, 0, 1);
    var outFade = b >= 0.999 ? 1 : clamp((1 - t) / 0.18, 0, 1);
    return Math.min(inFade, outFade);
  };

  function boot() {
    var THREE = window.THREE;
    if (!THREE) { bailOut(); return; }

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.97;

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0, 6.4);

    /* ---- soft studio environment, generated rather than downloaded ---- */
    var envCanvas = document.createElement('canvas');
    envCanvas.width = 64; envCanvas.height = 32;
    var ectx = envCanvas.getContext('2d');
    var grad = ectx.createLinearGradient(0, 0, 0, 32);
    grad.addColorStop(0, '#6d7f96');
    grad.addColorStop(0.45, '#2a3340');
    grad.addColorStop(1, '#0a0d12');
    ectx.fillStyle = grad; ectx.fillRect(0, 0, 64, 32);
    ectx.fillStyle = 'rgba(255,255,255,.85)';
    ectx.fillRect(6, 2, 20, 7);
    ectx.fillRect(40, 4, 14, 5);
    var envTex = new THREE.CanvasTexture(envCanvas);
    envTex.mapping = THREE.EquirectangularReflectionMapping;
    envTex.encoding = THREE.sRGBEncoding;
    /* A proper studio: a large env map with real softbox shapes gives crisp
       specular highlights. A 64px gradient just makes everything look flat. */
    function studioEnv() {
      var c = document.createElement('canvas'); c.width = 512; c.height = 256;
      var x = c.getContext('2d');
      var g = x.createLinearGradient(0, 0, 0, 256);
      g.addColorStop(0, '#aeb9c9'); g.addColorStop(0.42, '#4a5462');
      g.addColorStop(0.75, '#181d25'); g.addColorStop(1, '#05070a');
      x.fillStyle = g; x.fillRect(0, 0, 512, 256);
      function softbox(cx, cy, w, h, a) {
        var rg = x.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) / 2);
        rg.addColorStop(0, 'rgba(255,255,255,' + a + ')');
        rg.addColorStop(1, 'rgba(255,255,255,0)');
        x.fillStyle = rg; x.fillRect(cx - w / 2, cy - h / 2, w, h);
      }
      softbox(120, 52, 230, 130, 1);     // big key
      softbox(372, 74, 150, 90, 0.75);   // fill
      softbox(266, 30, 90, 52, 0.9);     // top kicker
      x.fillStyle = 'rgba(255,238,214,.35)'; x.fillRect(0, 96, 512, 10);
      var t = new THREE.CanvasTexture(c);
      t.mapping = THREE.EquirectangularReflectionMapping;
      t.encoding = THREE.sRGBEncoding;
      return t;
    }

    /* A soft contact shadow so objects sit in space instead of floating flat. */
    function contactShadow() {
      var c = document.createElement('canvas'); c.width = 256; c.height = 256;
      var x = c.getContext('2d');
      var rg = x.createRadialGradient(128, 128, 0, 128, 128, 128);
      rg.addColorStop(0, 'rgba(0,0,0,.55)');
      rg.addColorStop(0.45, 'rgba(0,0,0,.22)');
      rg.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = rg; x.fillRect(0, 0, 256, 256);
      var tex = new THREE.CanvasTexture(c);
      var m = new THREE.Mesh(
        new THREE.PlaneGeometry(7, 7),
        new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false })
      );
      m.rotation.x = -Math.PI / 2;
      return m;
    }

    var pmrem = new THREE.PMREMGenerator(renderer);
    var studioTex = studioEnv();
    scene.environment = pmrem.fromEquirectangular(studioTex).texture;
    studioTex.dispose();
    envTex.dispose();

    var key = new THREE.DirectionalLight(0xffffff, 1.75); key.position.set(3.2, 4.4, 5);
    var rim = new THREE.DirectionalLight(0xcfe0f2, 0.95); rim.position.set(-4.5, 1.2, -3.6);
    var fill = new THREE.DirectionalLight(0xffe9d2, 0.55); fill.position.set(-2, -2.4, 3);
    scene.add(key, rim, fill, new THREE.AmbientLight(0xffffff, 0.14));

    /* ---- the strip: a subdivided box pushed into a rounded stadium oval ---- */
    var LEN = 4.05, WID = 1.42;
    function stripGeometry(thickness) {
      var g = new THREE.BoxGeometry(LEN, thickness, WID, 160, 1, 26);
      var pos = g.attributes.position;
      for (var i = 0; i < pos.count; i++) {
        var x = pos.getX(i);
        var t = Math.abs(x) / (LEN / 2);
        // Flat through the middle, elliptical taper across the last 38%.
        var k = t < 0.62 ? 1 : Math.sqrt(Math.max(0, 1 - Math.pow((t - 0.62) / 0.38, 2)));
        pos.setZ(i, pos.getZ(i) * k);
      }
      pos.needsUpdate = true;
      g.computeVertexNormals();
      return g;
    }

    // One shared uniform bends every layer together, so the stack stays glued.
    var bendU = { value: 0 };
    function bendable(mat) {
      mat.onBeforeCompile = function (shader) {
        shader.uniforms.uBend = bendU;
        shader.vertexShader = 'uniform float uBend;\n' + shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n' +
          'float bx = transformed.x / ' + (LEN / 2).toFixed(3) + ';\n' +
          'transformed.y += uBend * (1.0 - bx * bx);\n' +
          'transformed.x *= 1.0 - abs(uBend) * 0.06;'
        );
      };
      mat.customProgramCacheKey = function () { return 'f3d-bend'; };
      return mat;
    }

    var fabric = bendable(new THREE.MeshPhysicalMaterial({
      color: 0x050608, roughness: 0.92, metalness: 0.0, clearcoat: 0.08, clearcoatRoughness: 0.9, sheen: 0.25, envMapIntensity: 0.35
    }));
    var adhesive = bendable(new THREE.MeshPhysicalMaterial({
      color: 0xd8dde6, roughness: 0.25, metalness: 0, transparent: true, opacity: 0.5, transmission: 0.35, envMapIntensity: 0.6
    }));
    var liner = bendable(new THREE.MeshPhysicalMaterial({
      color: 0xf4f1ea, roughness: 0.95, metalness: 0, transparent: true, opacity: 1, envMapIntensity: 0.7
    }));

    var tape = new THREE.Group();
    var layerFabric = new THREE.Mesh(stripGeometry(0.045), fabric);
    var layerGlue = new THREE.Mesh(stripGeometry(0.022), adhesive);
    var layerLiner = new THREE.Mesh(stripGeometry(0.03), liner);
    layerFabric.userData.rest = 0.030;
    layerGlue.userData.rest = -0.005;
    layerLiner.userData.rest = -0.038;
    layerGlue.scale.set(0.975, 1, 0.955);
    layerLiner.scale.set(0.975, 1, 0.955);
    [layerFabric, layerGlue, layerLiner].forEach(function (m) { tape.add(m); });
    scene.add(tape);

    /* ---- abstract head form: a suggestion, not a medical diagram ---- */
    var head = new THREE.Group();
    var skull = new THREE.Mesh(
      new THREE.SphereGeometry(1.5, 64, 48),
      new THREE.MeshPhysicalMaterial({ color: 0x070910, roughness: 0.98, metalness: 0.0, clearcoat: 0.0, envMapIntensity: 0.05 })
    );
    skull.scale.set(1, 1.22, 1.02);
    var jaw = new THREE.Mesh(new THREE.SphereGeometry(1.02, 48, 32), skull.material);
    jaw.scale.set(0.86, 0.76, 0.9);
    jaw.position.set(0, -1.16, 0.24);
    head.add(skull, jaw);
    head.visible = false;
    scene.add(head);

    /* ---- retail pack ----
       The product photo is shot on a white studio background with no alpha, so
       mapping it onto a face painted that white surround onto the pack. The
       artwork is drawn instead, which fills the pouch edge to edge. */
    // Drawn to match the real pouch: WHITE (not cream), navy|red|navy stripe
    // bands top and bottom, red lips outline, full spec block.
    function packTexture() {
      var c = document.createElement('canvas'); c.width = 700; c.height = 900;
      var x = c.getContext('2d');
      var NAVY = '#12315c', RED = '#d4172c';

      x.fillStyle = '#fbfbfa'; x.fillRect(0, 0, 700, 900);
      function bands(y, h) {
        x.fillStyle = NAVY; x.fillRect(283, y, 54, h); x.fillRect(363, y, 54, h);
        x.fillStyle = RED;  x.fillRect(337, y, 26, h);
      }
      bands(0, 172); bands(728, 172);

      x.strokeStyle = RED; x.lineWidth = 9; x.lineCap = 'round'; x.lineJoin = 'round';
      x.beginPath();
      x.moveTo(283, 288);
      x.quadraticCurveTo(318, 248, 350, 278);
      x.quadraticCurveTo(382, 248, 417, 288);
      x.quadraticCurveTo(350, 344, 283, 288);
      x.closePath(); x.stroke();

      x.fillStyle = NAVY; x.textAlign = 'center';
      x.font = '400 84px Georgia, "Times New Roman", serif';
      x.fillText('flexacare', 350, 386);
      x.strokeStyle = RED; x.lineWidth = 4;
      x.beginPath(); x.moveTo(312, 420); x.lineTo(388, 420); x.stroke();
      x.font = '400 52px Helvetica, Arial, sans-serif';
      x.fillText('M O U T H  T A P E', 350, 484);
      x.font = '400 27px Georgia, "Times New Roman", serif';
      x.fillText('Nightly breathing essential', 350, 540);
      x.fillText('Soft overnight lip support', 350, 574);
      x.strokeStyle = RED; x.lineWidth = 2.5;
      x.beginPath(); x.moveTo(208, 612); x.lineTo(492, 612); x.stroke();
      x.font = '400 23px Helvetica, Arial, sans-serif';
      x.fillText('3 5   S T R I P S    |    P R E M I U M   R A Y O N', 350, 644);
      x.beginPath(); x.moveTo(208, 666); x.lineTo(492, 666); x.stroke();
      x.font = '400 20px Helvetica, Arial, sans-serif';
      x.fillText('E X T E R N A L   U S E   O N L Y', 350, 700);
      x.font = '400 20px Georgia, serif';
      x.fillText('flexacareus.com', 350, 734);

      var t = new THREE.CanvasTexture(c);
      t.encoding = THREE.sRGBEncoding;
      t.anisotropy = renderer.capabilities.getMaxAnisotropy();
      return t;
    }

    /* ---- stand-up ziplock pouch ----
       Rounded outline, bevelled edges, and the face pillowed outward so it
       reads as a filled foil pouch rather than a box. */
    function pouchGeometry(w, h, depth) {
      var r = Math.min(w, h) * 0.11;
      var sh = new THREE.Shape();
      sh.moveTo(-w / 2 + r, -h / 2);
      sh.lineTo(w / 2 - r, -h / 2);
      sh.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + r);
      sh.lineTo(w / 2, h / 2 - r);
      sh.quadraticCurveTo(w / 2, h / 2, w / 2 - r, h / 2);
      sh.lineTo(-w / 2 + r, h / 2);
      sh.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - r);
      sh.lineTo(-w / 2, -h / 2 + r);
      sh.quadraticCurveTo(-w / 2, -h / 2, -w / 2, -h / 2 + r);

      var g = new THREE.ExtrudeGeometry(sh, {
        depth: depth, bevelEnabled: true, bevelThickness: depth * 0.28,
        bevelSize: depth * 0.3, bevelSegments: 5, curveSegments: 18, steps: 10
      });
      g.center();

      var pos = g.attributes.position, nrm = g.attributes.normal, uv = g.attributes.uv;
      for (var i = 0; i < pos.count; i++) {
        var x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
        // Pillow: push the faces out most in the middle, nothing at the seams.
        var nx = clamp(x / (w / 2), -1, 1), ny = clamp(y / (h / 2), -1, 1);
        var bulge = Math.pow(Math.max(0, 1 - nx * nx), 0.55) * Math.pow(Math.max(0, 1 - ny * ny), 0.55);
        // The bottom gusset is fuller than the top.
        bulge *= 0.75 + 0.35 * (1 - (y / (h / 2) + 1) / 2);
        pos.setZ(i, z + Math.sign(z || 1) * bulge * depth * 0.75);
        // Remap the front face so the artwork sits square on it.
        if (nrm.getZ(i) > 0.45) uv.setXY(i, (x + w / 2) / w, (y + h / 2) / h);
      }
      pos.needsUpdate = true; uv.needsUpdate = true;
      g.computeVertexNormals();
      return g;
    }

    function buildPouch(faceMat, bodyMat) {
      var grp = new THREE.Group();
      var H = 2.28, W = H * 0.98, D = 0.17;
      var body = new THREE.Mesh(pouchGeometry(W, H, D), [faceMat, bodyMat]);
      grp.add(body);

      // Zip seal plus the little lip of film above it.
      var zip = new THREE.Mesh(new THREE.BoxGeometry(W * 0.96, 0.055, D * 0.85), bodyMat);
      zip.position.set(0, H / 2 - 0.16, 0);
      var lip = new THREE.Mesh(pouchGeometry(W, 0.34, D * 0.42), [bodyMat, bodyMat]);
      lip.position.set(0, H / 2 + 0.12, 0);
      grp.add(zip, lip);
      return grp;
    }


    /* ---- the real pouch, as a photo ----
       Geometry can only approximate the pack. The photograph IS the pack, so it
       goes in the scene as a plane: background flood-filled away from the
       borders (which spares the white pouch body because that is enclosed by
       its own edge shading), then drawn UNLIT so every colour is exactly the
       pixel in the file, untouched by lights or tone mapping. */
    function cutoutCanvas(img, threshold) {
      var W = img.naturalWidth, H = img.naturalHeight;
      var c = document.createElement('canvas'); c.width = W; c.height = H;
      var x = c.getContext('2d'); x.drawImage(img, 0, 0);
      var id = x.getImageData(0, 0, W, H), d = id.data, N = W * H;
      var seen = new Uint8Array(N), bg = new Uint8Array(N), stack = [];
      function seed(i) { if (!seen[i]) { seen[i] = 1; stack.push(i); } }
      for (var i = 0; i < W; i++) { seed(i); seed((H - 1) * W + i); }
      for (var j = 0; j < H; j++) { seed(j * W); seed(j * W + W - 1); }
      while (stack.length) {
        var k = stack.pop(), p = k * 4;
        if (Math.min(d[p], d[p + 1], d[p + 2]) < threshold) continue;
        bg[k] = 1;
        var X = k % W, Y = (k - X) / W;
        if (X > 0) seed(k - 1); if (X < W - 1) seed(k + 1);
        if (Y > 0) seed(k - W); if (Y < H - 1) seed(k + W);
      }
      // Did the fill leak into the pouch? Sample the middle: it should be solid.
      var hit = 0, tot = 0;
      for (var yy = Math.floor(H * 0.3); yy < H * 0.7; yy += 3)
        for (var xx = Math.floor(W * 0.3); xx < W * 0.7; xx += 3) { tot++; if (!bg[yy * W + xx]) hit++; }
      if (hit / tot < 0.85) return null;
      // Erode one pixel so no bright halo survives at the cut edge.
      var er = new Uint8Array(N);
      for (var q = 0; q < N; q++) {
        if (bg[q]) continue;
        var X2 = q % W, Y2 = (q - X2) / W;
        if ((X2 > 0 && bg[q - 1]) || (X2 < W - 1 && bg[q + 1]) || (Y2 > 0 && bg[q - W]) || (Y2 < H - 1 && bg[q + W])) er[q] = 1;
      }
      var minX = W, minY = H, maxX = 0, maxY = 0;
      for (var r = 0; r < N; r++) {
        if (bg[r] || er[r]) { d[r * 4 + 3] = 0; continue; }
        var rx = r % W, ry = (r - rx) / W;
        if (rx < minX) minX = rx; if (rx > maxX) maxX = rx;
        if (ry < minY) minY = ry; if (ry > maxY) maxY = ry;
      }
      x.putImageData(id, 0, 0);
      // Soften the alpha edge by a pixel, then crop tight to the pouch.
      var pad = 6;
      var cw = Math.min(W, maxX - minX + 1 + pad * 2), ch = Math.min(H, maxY - minY + 1 + pad * 2);
      var out = document.createElement('canvas'); out.width = cw; out.height = ch;
      var ox = out.getContext('2d');
      ox.filter = 'blur(0.6px)';
      ox.drawImage(c, Math.max(0, minX - pad), Math.max(0, minY - pad), cw, ch, 0, 0, cw, ch);
      out.dataset.aspect = (cw / ch).toFixed(4);
      return out;
    }

    function loadPouchPhoto(src, onReady) {
      if (!src) return;
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        var cv = cutoutCanvas(img, 234) || cutoutCanvas(img, 248);
        if (!cv) return;
        var tex = new THREE.CanvasTexture(cv);
        tex.encoding = THREE.sRGBEncoding;
        tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.generateMipmaps = true;
        onReady(tex);
      };
      img.src = src;
    }

    var pack = new THREE.Group();
    var creamMat = new THREE.MeshPhysicalMaterial({ color: 0xE8E2D8, roughness: 0.74, metalness: 0.0, clearcoat: 0.06, clearcoatRoughness: 0.68, envMapIntensity: 0.78 });
    var frontMat = new THREE.MeshPhysicalMaterial({ map: packTexture(), roughness: 0.62, metalness: 0.0, clearcoat: 0.08, clearcoatRoughness: 0.62, envMapIntensity: 0.80 });
    // Box face order: +X, -X, +Y, -Y, +Z (toward camera), -Z
    var drawnPouch = buildPouch(frontMat, creamMat);
    pack.add(drawnPouch);
    // Put the real artwork on the pouch geometry. It used to swap in a flat
    // unlit plane, which read as a dull cut-out with hard aliased edges — the
    // silhouette came from an alpha test rather than from geometry.
    loadPouchPhoto(PACK_SRC, function (tex) {
      frontMat.map = tex;
      frontMat.needsUpdate = true;
    });
    pack.visible = false;
    scene.add(pack);


    // Extra strips that swarm the pack in the final act.
    var swarm = [];
    for (var i = 0; i < 7; i++) {
      var m = new THREE.Mesh(stripGeometry(0.05), fabric);
      m.scale.setScalar(0.24);
      m.visible = false;
      m.userData.seed = Math.random() * Math.PI * 2;
      m.userData.radius = 3.1 + Math.random() * 1.8;
      m.userData.y = (Math.random() - 0.5) * 2.8;
      swarm.push(m);
      scene.add(m);
    }

    /* ---------------- sizing ---------------- */
    function resize() {
      var w = stage.clientWidth, h = stage.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      // Pull the camera back on narrow screens so nothing crops on a phone.
      camera.fov = w / h < 0.8 ? 52 : 38;
      camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener('resize', resize, { passive: true });

    /* ---------------- scroll ---------------- */
    var target = 0, current = 0, visible = false, raf = null;

    function readScroll() {
      var r = root.getBoundingClientRect();
      var travel = root.offsetHeight - window.innerHeight;
      target = travel > 0 ? clamp(-r.top / travel, 0, 1) : 0;
    }
    window.addEventListener('scroll', readScroll, { passive: true });
    readScroll();

    var io = new IntersectionObserver(function (e) {
      visible = e[0].isIntersecting;
      if (visible && !raf) raf = requestAnimationFrame(tick);
    }, { rootMargin: '20% 0px' });
    io.observe(root);

    function tick() {
      raf = visible ? requestAnimationFrame(tick) : null;
      // Damping is what gives "stops when you stop, reverses smoothly".
      current = lerp(current, target, 0.075);
      draw(current);
      renderer.render(scene, camera);
    }

    function draw(p) {
      var t = performance.now() * 0.001;

      // ---- 1. hero: slow rotation in space
      var intro = seg(p, 0, 0.14);
      // ---- 2/3. explode then reassemble
      var out = ease(seg(p, 0.14, 0.30));
      var back = ease(seg(p, 0.30, 0.42));
      var spread = (out - back) * 1.0;
      // ---- 4. flex
      var flex = seg(p, 0.42, 0.56);
      bendU.value = Math.sin(flex * Math.PI) * 0.42 * Math.sin(t * 1.6);
      // ---- 5. to the face
      var toFace = ease(seg(p, 0.56, 0.72));
      // ---- 6/7. pack
      var toPack = ease(seg(p, 0.72, 0.88));
      var finale = ease(seg(p, 0.88, 1));

      // Inner layers only appear while the stack is separated, otherwise the
      // white liner peeks past the fabric and it reads as a white sticker.
      var reveal = clamp(Math.abs(spread) * 7, 0, 1);
      adhesive.opacity = 0.5 * reveal;
      liner.opacity = reveal;
      layerGlue.visible = reveal > 0.01;
      layerLiner.visible = reveal > 0.01;

      // layer separation
      tape.children.forEach(function (m, idx) {
        var rest = m.userData.rest;
        m.position.y = rest + spread * (idx - 1) * -1.55;
        m.position.z = spread * (idx - 1) * 0.14;
        m.rotation.z = spread * (idx - 1) * 0.06;
      });

      // tape transform across the whole story
      var faceY = -0.30, faceZ = 0.35;
      tape.position.set(
        0,
        lerp(Math.sin(t * 0.9) * 0.06, faceY, toFace),
        lerp(0, faceZ, toFace)
      );
      tape.rotation.x = lerp(0.34 - intro * 0.18 + spread * 0.30 + Math.sin(t * 0.5) * 0.05, 0.14, toFace);
      tape.rotation.y = lerp(intro * Math.PI * 0.85 + Math.sin(t * 0.33) * 0.20, 0, toFace);
      tape.rotation.z = lerp(0.04 + Math.sin(t * 0.41) * 0.03, 0, toFace);
      var tapeScale = lerp(1, 0.52, toFace) * (1 - toPack * 0.92);
      tape.scale.setScalar(Math.max(0.0001, tapeScale));
      tape.visible = tapeScale > 0.02 && toPack < 0.86;

      // head fades in for the application beat, out again for the pack
      head.visible = toFace > 0.01 && toPack < 0.25;
      if (head.visible) {
        head.position.set(0, 0.55 + (1 - toFace) * 1.2, -2.6);
        head.rotation.y = Math.sin(t * 0.25) * 0.12;
        head.scale.setScalar(lerp(1.0, 1.22, toFace) * clamp(1 - toPack * 4, 0, 1));
        skull.material.opacity = 1;
      }

      // pack rises, swarm orbits in
      pack.visible = toPack > 0.01;
      if (pack.visible) {
        pack.position.set(0, lerp(-3.2, 1.06, toPack) + Math.sin(t * 0.7) * 0.06 * finale, 0);
        pack.rotation.y = lerp(-0.28, 0.05 + Math.sin(t * 0.35) * 0.05, toPack);
        pack.rotation.x = lerp(0.12, 0.0, toPack);
        pack.scale.setScalar(lerp(0.5, 0.66, toPack));
      }
      swarm.forEach(function (m, idx) {
        var live = toPack > 0.02 && finale < 0.98;
        m.visible = live;
        if (!live) return;
        var a = m.userData.seed + t * 0.5 + idx;
        // Spiral inward as the pack assembles, then disappear into it.
        var r = m.userData.radius * (1 - toPack * 0.86) * (1 - finale);
        m.position.set(Math.cos(a) * r, m.userData.y * (1 - toPack * 0.8) + Math.sin(a * 0.7) * 0.3, Math.sin(a) * r * 0.6);
        m.rotation.set(a * 0.6, a, Math.sin(a) * 0.4);
        m.scale.setScalar(0.24 * (1 - finale));
      });

      // camera drift
      camera.position.z = lerp(6.4, 5.9, toFace) + finale * 0.5 + (1 - intro) * 0.3;
      camera.position.y = lerp(0, 0.28, toFace) - finale * 0.1;
      camera.lookAt(0, toFace > 0.5 && toPack < 0.5 ? -0.1 : 0, 0);

      // overlay copy
      chapters.forEach(function (c) {
        var a = parseFloat(c.dataset.from), b = parseFloat(c.dataset.to);
        var o = band(p, a, b);
        c.style.opacity = o;
        c.style.transform = 'translate(-50%, ' + ((1 - o) * 16) + 'px)';
        c.style.pointerEvents = o > 0.6 ? 'auto' : 'none';
        // A chapter faded to 0 is otherwise still in the tab order, so a keyboard
        // user lands on an invisible link. inert removes it from the tab order
        // and the accessibility tree together.
        c.inert = o < 0.05;
      });
    }

    root.setAttribute('data-f3d-ready', '');
    draw(0);
    renderer.render(scene, camera);
  }
})();
